import { useQuery } from '@tanstack/react-query';
import getCameras from "@/data/calls/camera/fetchCameras";
import getThings from "@/data/calls/thing/fetchThings"; 
import { ICamera } from '@/data/interfaces/camera';
import { IThing, IThingSimple } from '@/data/interfaces/thing';

interface UseTalkdownDataProps {
  locationId?: number;
  cameraId?: string;
  context: 'queue' | 'alerts' | 'cameras';
  existingThings?: IThingSimple[];
  existingCameras?: ICamera[];
}

interface TalkdownData {
  things: IThing[];
  cameras: ICamera[];
  isLoading: boolean;
  error: any;
}

export const useTalkdownData = ({
  locationId,
  cameraId,
  context,
  existingThings = [],
  existingCameras = []
}: UseTalkdownDataProps): TalkdownData => {
  
  const isEnabled = !!locationId && context === 'cameras';

  // Fetch Cameras
  const { 
    data: camerasData, 
    isLoading: isCamerasLoading, 
    error: camerasError 
  } = useQuery(
    ['location-cameras', locationId],
    async () => {
      if (locationId == null) {
        throw new Error('locationId is required to fetch cameras');
      }

      const allCameras: ICamera[] = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        const camerasResponse = await getCameras({ page, LocationId: String(locationId) });
        const camerasArray = camerasResponse?.cameras ?? [];

        if (camerasArray.length > 0) {
          allCameras.push(...camerasArray);
          page++;
        } else {
          hasMore = false;
        }
      }

      return { cameras: allCameras };
    },
    {
      enabled: isEnabled,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      retry: 2,
      retryDelay: 1000,
    }
  );

  // Fetch Things
  const { 
    data: thingsData, 
    isLoading: isThingsLoading, 
    error: thingsError 
  } = useQuery(
    ['location-things', locationId],
    () => {
      if (locationId == null) {
        throw new Error('locationId is required to fetch things');
      }
      return getThings({ LocationId: String(locationId) });
    },
    {
      enabled: isEnabled,
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      retry: 2,
      retryDelay: 1000,
    }
  );

  if (context === 'queue' || context === 'alerts') {
    const convertedThings: IThing[] = (existingThings || []).map(thing => ({
      id: parseInt(thing.id),
      name: thing.name,
      username: '',
      password: '',
      general_description: '',
    } as IThing));
    
    return {
      things: convertedThings,
      cameras: existingCameras,
      isLoading: false,
      error: null
    };
  }

  return {
    things: thingsData?.things || [],
    cameras: camerasData?.cameras || [],
    isLoading: isCamerasLoading || isThingsLoading,
    error: camerasError || thingsError
  };
};